import { Alert, Box, Button } from '@mui/material'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UnauthorizedError } from '../auth/UnauthorizedError'
import { setSessionExpired } from '../store/authSlice'
import { useAppDispatch } from '../store/store.hooks'

interface ErrorBoundaryInnerProps {
  children: React.ReactNode
  fallback: (reset: () => void) => React.ReactNode
  onError: (error: Error) => void
}

class ErrorBoundaryInner extends React.Component<ErrorBoundaryInnerProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryInnerProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  componentDidCatch(error: Error): void {
    this.props.onError(error)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback(() => this.setState({ hasError: false }))
    }
    return this.props.children
  }
}

const QueryErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const handleError = (error: Error) => {
    if (error instanceof UnauthorizedError) {
      dispatch(setSessionExpired(true))
    }
  }

  const fallback = (reset: () => void) => (
    <Box sx={{ p: 3 }}>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={reset}>
            {t('buttons.retry')}
          </Button>
        }
      >
        {t('errors.loadingError')}
      </Alert>
    </Box>
  )

  return (
    <QueryErrorResetBoundary>
      {({ reset: resetQueryErrors }) => (
        <ErrorBoundaryInner
          onError={handleError}
          fallback={(resetBoundary) =>
            fallback(() => {
              resetQueryErrors()
              resetBoundary()
            })
          }
        >
          {children}
        </ErrorBoundaryInner>
      )}
    </QueryErrorResetBoundary>
  )
}

export default QueryErrorBoundary
